import React from "react";
import { useLocation } from "@reach/router";
import { NoAccessMessage } from "newui/components/AccessGate";
import { useHasNoRbacAccess } from "hooks/auth";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";

interface RolelessRouteGuardProps {
	children: React.ReactNode;
}

/**
 * PRO-737 outcome 2 (second half) — a user holding no RBAC standing who types a
 * URL directly gets "Access denied" rather than the page.
 *
 * The menu is already inert for such a user ({@see AppMenuLink}) and they land
 * on My Profile at login ({@see useNavigationPaths}), so this closes the one
 * remaining route in: the address bar. Wrapping the member route tree means a
 * page added later is denied by default instead of quietly escaping the gate.
 *
 * This is an affordance, not the access control. Every request the denied page
 * would have made is refused server-side by EnforceRolelessRestriction; this
 * exists so the user sees one calm message instead of a broken page firing 403
 * toasts.
 */
export const RolelessRouteGuard = ({ children }: RolelessRouteGuardProps) => {
	const hasNoRbacAccess = useHasNoRbacAccess();
	const { memberPaths } = useNavigationPaths();
	const location = useLocation();

	// Compared against the centralised path rather than a hardcoded segment, so
	// moving the profile route cannot silently lock the user out of the only
	// page they can open.
	const profilePath = memberPaths?.myProfile;
	const isOnProfile =
		profilePath !== undefined &&
		profilePath !== null &&
		location.pathname.startsWith(profilePath);

	if (hasNoRbacAccess && !isOnProfile) {
		return (
			<NoAccessMessage
				data-testid="roleless-access-denied"
				description="Your account currently has no permissions configured. Open My Profile to see who to contact about getting access."
			/>
		);
	}

	return <>{children}</>;
};
